import { inject, Injectable } from "@angular/core";
import { isPropertyDefined } from "@recursyve/nice-ts-utils";
import { NiceAsyncTypeaheadProvider, NiceAsyncTypeaheadSearchResult } from "@recursyve/nice-ui-kit.v2";
import { lastValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "../../api.service";
import { FindOptionsDto } from "../../common/dto/find-options.dto";
import { FindDistributorsDto } from "../dto/find-distributors.dto";
import { DistributorsDataTypes } from "../enums/distributors-data-types.enum";
import { DistributorsStatus } from "../enums/distributors-status.enum";
import { Distributors } from "../models/distributors.model";

export interface DistributorTypeaheadOptions extends FindOptionsDto {
    status?: DistributorsStatus;
}

@Injectable()
export class DistributorTypeaheadApi extends NiceAsyncTypeaheadProvider<Distributors> {
    private readonly api = inject(ApiService);

    public readonly resource = "distributors";

    private readonly pageSize = 20;

    public override async getById(id: number): Promise<Distributors> {
        return lastValueFrom(this.api.distributors.getById(id));
    }

    public override search(
        searchQuery: string,
        page: number,
        options?: DistributorTypeaheadOptions
    ): Observable<NiceAsyncTypeaheadSearchResult<Distributors>> {
        const dto: FindDistributorsDto = {
            searchQuery,
            page,
            pageSize: this.pageSize,
            distributorsDataType: DistributorsDataTypes.SEARCH
        };

        if (isPropertyDefined(options, "status")) {
            dto.status = options.status;
        }

        if (isPropertyDefined(options, "ignoreIds")) {
            dto.ignoreIds = options.ignoreIds.map((x) => x);
        }

        return this.api.distributors
            .search(dto)
            .pipe(map((items) => ({ items, nextPage: items.length === this.pageSize ? page + 1 : null })));
    }

    public override format(item: Distributors): string {
        if (typeof item.name === "string") {
            return item.name;
        }

        return "";
    }
}
